import Icon from "assets/icons";
import ParticipationLocationType from "shared/enums/ParticipationLocationType";
import { ProgramType } from "shared/enums/ProgramType";
import { ApiResourceAsset } from "../types/ApiResourceAsset";
import Image from "../form/core/image";

// Convert hex to RGB
function hexToRgb(hex: string) {
  let r = 0,
    g = 0,
    b = 0;
  // Remove '#' if it exists
  if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  }
  return { r, g, b, rgb: `${r}, ${g}, ${b}` };
}

// Convert RGB to hex
function rgbToHex(r: number, g: number, b: number) {
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

// Lighten or darken color
function adjustColor(color: string, percent: number) {
  const { r, g, b } = hexToRgb(color);
  const newR = Math.min(255, Math.max(0, r + r * percent));
  const newG = Math.min(255, Math.max(0, g + g * percent));
  const newB = Math.min(255, Math.max(0, b + b * percent));
  return rgbToHex(Math.round(newR), Math.round(newG), Math.round(newB));
}

// Mix two colors by a percentage
function mixColors(color1: string, color2: string, percentage: number) {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  const r = Math.round(rgb1.r * (1 - percentage) + rgb2.r * percentage);
  const g = Math.round(rgb1.g * (1 - percentage) + rgb2.g * percentage);
  const b = Math.round(rgb1.b * (1 - percentage) + rgb2.b * percentage);

  return rgbToHex(r, g, b);
}

export default function getProgramTypeIcon({
  programType,
  participationLocationType,
  programIconImage,
  additionalClassname
}: {
  programType?: ProgramType;
  participationLocationType?: ParticipationLocationType;
  programIconImage?: ApiResourceAsset;
  additionalClassname?: string;
}) {
  if (programIconImage) {
    return (
      <Image
        className={additionalClassname}
        src={programIconImage.url}
        showLoadingAnimation={true}
        alt={programType}
        height={50}
        width={50}
        fallbackRenderer={getProgramTypeIcon({ programType, participationLocationType })}
      />
    );
  }
  switch (programType) {
    case ProgramType.BUYERS_PROGRAM:
    case ProgramType.IN_PERSON_MEETUP_PROGRAM:
      return <Icon.InPerson />;
    case ProgramType.MEETUP_PROGRAM:
    case ProgramType.TRADE_PROGRAM:
      if (participationLocationType === ParticipationLocationType.VIRTUAL) {
        return <Icon.VirtualMeeting />;
      } else {
        return <Icon.InPerson />;
      }
    case ProgramType.TABLETALK_PROGRAM:
      if (participationLocationType === ParticipationLocationType.VIRTUAL) {
        return <Icon.VirtualMeeting />;
      } else {
        return <Icon.TableTalk />;
      }
    case ProgramType.ASSOCIATED_PROGRAM:
      return <Icon.AssociatedProgram />;
    case ProgramType.CONNECTIONS_PROGRAM:
      return <Icon.ProgramConnection />;
    default:
      return "";
  }
}

export function getProgramColorStyleProps({
  programThemeColor = "#d4d4d4"
}: {
  programThemeColor: string;
}): React.CSSProperties {
  const programPrimaryLighter = adjustColor(programThemeColor, 0.3);
  const programPrimaryBorder = mixColors(programThemeColor, "#ffffff", 0);
  const programPrimaryBorderDark = mixColors(programThemeColor, "#000000", 0.3);
  const programPrimaryCardBG = mixColors(programThemeColor, "#ffffff", 0.96);
  const programPrimaryHeaderBG = mixColors(programThemeColor, "#ffffff", 0.6);
  const programPrimaryGradColor_1 = mixColors(programThemeColor, programThemeColor, -0.3);
  const programPrimaryGradColor_2 = mixColors(programThemeColor, adjustColor(programThemeColor, 0.15), -1);
  const programPrimaryMyActionBG = mixColors(programThemeColor, "#ffffff", 0.8);
  const programPrimaryRGB = hexToRgb(programThemeColor).rgb;
  const programPrimaryMat = mixColors(programThemeColor, adjustColor(programThemeColor, 0.2), -1);
  return {
    "--programPrimaryColor": programThemeColor,
    "--programPrimaryBorder": programPrimaryBorder,
    "--programPrimaryBorderDark": programPrimaryBorderDark,
    "--programPrimaryLighter": programPrimaryLighter,
    "--programPrimaryCardBG": programPrimaryCardBG,
    "--programPrimaryHeaderBG": programPrimaryHeaderBG,
    "--programPrimaryGradColor_1": programPrimaryGradColor_1,
    "--programPrimaryGradColor_2": programPrimaryGradColor_2,
    "--programPrimaryMyActionBG": programPrimaryMyActionBG,
    "--programPrimaryRGB": programPrimaryRGB,
    "--programPrimaryMat": programPrimaryMat
  } as React.CSSProperties;
}

export function getClassByProgramType({ programType }: { programType?: ProgramType }) {
  switch (programType) {
    case ProgramType.BUYERS_PROGRAM:
    case ProgramType.TRADE_PROGRAM:
      return "buyersProgram";
    case ProgramType.IN_PERSON_MEETUP_PROGRAM:
    case ProgramType.MEETUP_PROGRAM:
      return "meetupProgram";
    case ProgramType.TABLETALK_PROGRAM:
      return "tabletalkProgram";
    default:
      return "";
  }
}

export function getIconByParticipationLocationType(participationLocationType: ParticipationLocationType) {
  switch (participationLocationType) {
    case ParticipationLocationType.TABLE:
      return <Icon.TableChair />;
    case ParticipationLocationType.BOOTH:
      return <Icon.Store />;
    case ParticipationLocationType.BOOTH_AND_TABLE:
    case ParticipationLocationType.ACTIVITY:
      return <Icon.Location />;
    case ParticipationLocationType.VIRTUAL:
      return <Icon.VirtualMeeting />;
    default:
      return null;
  }
}

export const ProgramTypeTextColorClassMap: { [key in ProgramType]: string } = {
  [ProgramType.MEETUP_PROGRAM]: "meetup--textColor",
  [ProgramType.IN_PERSON_MEETUP_PROGRAM]: "meetup--textColor",
  [ProgramType.TABLETALK_PROGRAM]: "tableTalk--textColor",
  [ProgramType.BUYERS_PROGRAM]: "buyers--textColor",
  [ProgramType.ASSOCIATED_PROGRAM]: "associated--textColor",
  [ProgramType.TRADE_PROGRAM]: "trade--textColor",
  [ProgramType.CONNECTIONS_PROGRAM]: "connection--textColor"
};

export const ProgramTypeBorderColorClassMap: { [key in ProgramType]: string } = {
  [ProgramType.MEETUP_PROGRAM]: "meetup--borderColor",
  [ProgramType.IN_PERSON_MEETUP_PROGRAM]: "meetup--borderColor",
  [ProgramType.TABLETALK_PROGRAM]: "tableTalk--borderColor",
  [ProgramType.BUYERS_PROGRAM]: "buyers--borderColor",
  [ProgramType.ASSOCIATED_PROGRAM]: "associated--borderColor",
  [ProgramType.TRADE_PROGRAM]: "trade--borderColor",
  [ProgramType.CONNECTIONS_PROGRAM]: "connection--borderColor"
};
export const ProgramTypeCardBorderColorClassMap: { [key in ProgramType]: string } = {
  [ProgramType.MEETUP_PROGRAM]: "meetup--cardBorderColor",
  [ProgramType.IN_PERSON_MEETUP_PROGRAM]: "meetup--cardBorderColor",
  [ProgramType.TABLETALK_PROGRAM]: "tableTalk--cardBorderColor",
  [ProgramType.BUYERS_PROGRAM]: "buyers--cardBorderColor",
  [ProgramType.ASSOCIATED_PROGRAM]: "associated--cardBorderColor",
  [ProgramType.TRADE_PROGRAM]: "trade--cardBorderColor",
  [ProgramType.CONNECTIONS_PROGRAM]: "connection--cardBorderColor"
};
export const ProgramTypeListBorderColorClassMap: { [key in ProgramType]: string } = {
  [ProgramType.MEETUP_PROGRAM]: "meetup--listBorderColor",
  [ProgramType.IN_PERSON_MEETUP_PROGRAM]: "meetup--listBorderColor",
  [ProgramType.TABLETALK_PROGRAM]: "tableTalk--listBorderColor",
  [ProgramType.BUYERS_PROGRAM]: "buyers--listBorderColor",
  [ProgramType.ASSOCIATED_PROGRAM]: "associated--listBorderColor",
  [ProgramType.TRADE_PROGRAM]: "trade--listBorderColor",
  [ProgramType.CONNECTIONS_PROGRAM]: "connection--listBorderColor"
};
export const ProgramTypeBgColorClassMap: { [key in ProgramType]: string } = {
  [ProgramType.MEETUP_PROGRAM]: "meetup--bgColor",
  [ProgramType.IN_PERSON_MEETUP_PROGRAM]: "meetup--bgColor",
  [ProgramType.TABLETALK_PROGRAM]: "tableTalk--bgColor",
  [ProgramType.BUYERS_PROGRAM]: "buyers--bgColor",
  [ProgramType.ASSOCIATED_PROGRAM]: "associated--bgColor",
  [ProgramType.TRADE_PROGRAM]: "trade--bgColor",
  [ProgramType.CONNECTIONS_PROGRAM]: "connection--bgColor"
};
export const ProgramTypeBgMediumColorClassMap: { [key in ProgramType]: string } = {
  [ProgramType.MEETUP_PROGRAM]: "meetup--bgMediumColor",
  [ProgramType.IN_PERSON_MEETUP_PROGRAM]: "meetup--bgMediumColor",
  [ProgramType.TABLETALK_PROGRAM]: "tableTalk--bgMediumColor",
  [ProgramType.BUYERS_PROGRAM]: "buyers--bgMediumColor",
  [ProgramType.ASSOCIATED_PROGRAM]: "associated--bgMediumColor",
  [ProgramType.TRADE_PROGRAM]: "trade--bgMediumColor",
  [ProgramType.CONNECTIONS_PROGRAM]: "connection--bgMediumColor"
};
